// components/CookieSettings.tsx
"use client";

import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { InfoIcon } from "lucide-react";
import { useCookieConsent } from "@/hooks/use-cookie-consent";

interface CookieSettingsProps {
  showTitle?: boolean;
  showSaveButton?: boolean;
  onSave?: () => void;
}

const cookieTypes = [
  {
    id: "necessary",
    title: "Necessary Cookies",
    description:
      "These cookies are essential for the website to function properly. They enable basic functions like page navigation, security, and maintaining your preferences. The website cannot function properly without these cookies.",
    required: true,
  },
  {
    id: "analytics",
    title: "Analytics Cookies",
    description:
      "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website structure, content, and user experience.",
    required: false,
  },
  {
    id: "marketing",
    title: "Marketing Cookies",
    description:
      "These cookies are used to track visitors across websites to display relevant advertisements. They help make advertising more engaging to users and more valuable to publishers and advertisers.",
    required: false,
  },
];

export default function CookieSettings({
  showTitle = true,
  showSaveButton = true,
  onSave,
}: CookieSettingsProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const { preferences, updatePreferences } = useCookieConsent();

  const handleSave = async () => {
    setSaving(true);
    try {
      const success = await updatePreferences(preferences);
      if (success) {
        toast({
          title: "Success",
          description: "Your cookie preferences have been saved.",
          variant: "success",
        });
        onSave?.();
      } else {
        throw new Error("Failed to save preferences");
      }
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className='w-full'>
      {showTitle && (
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            Cookie Settings
            <InfoIcon className='h-5 w-5 text-muted-foreground' />
          </CardTitle>
          <CardDescription>
            Manage your cookie preferences. Some cookies are necessary for the
            website to function and cannot be disabled.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className='space-y-6'>
        {cookieTypes.map((cookieType) => (
          <div key={cookieType.id} className='flex flex-col space-y-2'>
            <div className='flex items-center justify-between'>
              <div className='space-y-0.5'>
                <div className='text-sm font-medium'>
                  {cookieType.title}
                  {cookieType.required && (
                    <span className='ml-2 text-xs text-muted-foreground'>
                      (Required)
                    </span>
                  )}
                </div>
                <div className='text-sm text-muted-foreground'>
                  {cookieType.description}
                </div>
              </div>
              <Switch
                checked={preferences[cookieType.id as keyof typeof preferences]}
                onCheckedChange={(checked) => {
                  if (cookieType.id === "necessary") return;
                  updatePreferences({
                    ...preferences,
                    [cookieType.id]: checked,
                  });
                }}
                disabled={cookieType.required || saving}
              />
            </div>
          </div>
        ))}

        {showSaveButton && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className='w-full sm:w-auto mt-4'
          >
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
